import { useMemo } from 'react';
import { Modal, Form, Input, Select, DatePicker, InputNumber, Button, message } from '../../ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { request } from '../../api/client';

const TYPES = [
  { value: 'RETAILER_MEET', label: 'Retailer meet' },
  { value: 'FARMER_MEET', label: 'Farmer meet' },
  { value: 'TRAINING', label: 'Training' },
  { value: 'SEMINAR', label: 'Seminar' },
  { value: 'PRODUCT_LAUNCH', label: 'Product launch' },
  { value: 'FIELD_DAY', label: 'Field day' },
  { value: 'MEETING', label: 'Meeting' },
  { value: 'OTHER', label: 'Other' },
];
const CADENCES = [
  { value: 'ONE_TIME', label: 'One-time' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'YEARLY', label: 'Yearly' },
];

/** Create / edit an event (the core planning fields; outcome is posted on the detail page). */
export function EventFormModal({ open, onClose, record }: { open: boolean; onClose: () => void; record?: any | null }) {
  const qc = useQueryClient();
  const [form] = Form.useForm();
  const isEdit = !!record;

  const { data: territories } = useQuery({
    queryKey: ['territories'],
    queryFn: () => request<any[]>({ url: '/territories' }),
    enabled: open,
  });

  const territoryOptions = useMemo(() => {
    let opts = (territories ?? []).map((t: any) => ({ value: String(t.id), label: `${t.code} · ${t.name}`, level: t.level }));
    // Surface the most specific levels first; all levels are allowed for an event.
    const order: Record<string, number> = { beat: 0, district: 1, state: 2, zone: 3 };
    opts = opts.sort((a, b) => (order[a.level] ?? 9) - (order[b.level] ?? 9));
    return opts;
  }, [territories]);

  // Seed via initialValues + a per-target key (below), NOT setFieldsValue in an effect: the ui Form
  // seeds its store from initialValues on mount and the Radix Modal remounts the form each open, so an
  // open-effect seed raced the remount (blank fields) and let a prior event's values leak in (stale).
  const initialValues = useMemo(
    () =>
      record
        ? {
            title: record.title,
            type: record.type,
            cadence: record.cadence,
            territoryId: record.territoryId ? String(record.territoryId) : undefined,
            when: [record.startAt ? dayjs(record.startAt) : null, record.endAt ? dayjs(record.endAt) : null],
            venue: record.venue,
            capacity: record.capacity ?? undefined,
            budget: record.budget != null ? Number(record.budget) : undefined,
            description: record.description,
          }
        : { type: 'RETAILER_MEET', cadence: 'ONE_TIME' },
    [record],
  );

  const save = useMutation({
    mutationFn: (vals: any) => {
      const [start, end] = vals.when ?? [];
      const payload: any = {
        title: vals.title,
        type: vals.type,
        cadence: vals.cadence,
        territoryId: vals.territoryId ?? undefined,
        venue: vals.venue || undefined,
        startAt: start ? dayjs(start).toISOString() : undefined,
        endAt: end ? dayjs(end).toISOString() : undefined,
        capacity: vals.capacity ?? undefined,
        budget: vals.budget ?? undefined,
        description: vals.description || undefined,
      };
      return isEdit
        ? request({ url: `/events/${record.id}`, method: 'PATCH', data: payload })
        : request({ url: '/events', method: 'POST', data: payload });
    },
    onSuccess: () => {
      message.success(isEdit ? 'Event updated' : 'Event created');
      qc.invalidateQueries({ queryKey: ['events'] });
      qc.invalidateQueries({ queryKey: ['event'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to save event'),
  });

  const submit = async () => {
    const vals = await form.validateFields();
    save.mutate(vals);
  };

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={isEdit ? `Edit event · ${record?.code}` : 'New event / meet'}
      width={640}
      footer={<>
        <Button variant="default" onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={save.isPending} onClick={submit}>{isEdit ? 'Save changes' : 'Create event'}</Button>
      </>}
    >
      <Form key={record?.id ?? 'new'} form={form} layout="vertical" initialValues={initialValues}>
        <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Enter a title' }]}>
          <Input placeholder="e.g. Kharif farmer meet — Pune East" />
        </Form.Item>
        <div style={{ display: 'flex', gap: 12 }}>
          <Form.Item name="type" label="Type" style={{ flex: 1 }} rules={[{ required: true }]}>
            <Select options={TYPES} />
          </Form.Item>
          <Form.Item name="cadence" label="Cadence" style={{ flex: 1 }} rules={[{ required: true }]}>
            <Select options={CADENCES} />
          </Form.Item>
        </div>
        <Form.Item name="territoryId" label="Territory" rules={[{ required: true, message: 'Pick the territory this meet is for' }]}
          extra="Invitees will be the retailers & farmers of this territory. You can only place an event in an area you cover.">
          <Select showSearch optionFilterProp="label" placeholder="Select territory" options={territoryOptions} />
        </Form.Item>
        <Form.Item name="when" label="When (start — end)" rules={[{ required: true, message: 'Pick the date & time' }]}>
          <DatePicker.RangePicker showTime format="DD MMM YYYY, hh:mm A" style={{ width: '100%' }} allowEmpty={[false, true]} />
        </Form.Item>
        <Form.Item name="venue" label="Venue">
          <Input placeholder="Venue / address (optional)" />
        </Form.Item>
        <div style={{ display: 'flex', gap: 12 }}>
          <Form.Item name="capacity" label="Expected attendees" style={{ flex: 1 }}>
            <InputNumber min={0} style={{ width: '100%' }} placeholder="e.g. 50" />
          </Form.Item>
          <Form.Item name="budget" label="Budget (₹)" style={{ flex: 1 }}>
            <InputNumber min={0} style={{ width: '100%' }} placeholder="optional" />
          </Form.Item>
        </div>
        <Form.Item name="description" label="Agenda / about">
          <Input.TextArea rows={3} placeholder="What this meet is about, planned agenda…" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
